"use client";

import { initPerformanceMonitoring, markPerformance, measurePerformance, reportWebVitals } from "@/instrumentation-client";
import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";

/**
 * Client-side performance monitoring component
 *
 * Initializes web vitals tracking and performance observers.
 * Add this to your root layout to enable monitoring.
 */
export function PerformanceMonitor() {
    const pathname = usePathname();
    const initialized = useRef(false);

    // Initialize monitoring on mount (once)
    useEffect(() => {
        if (initialized.current) {
            return;
        }
        initialized.current = true;

        initPerformanceMonitoring();

        // Set up Web Vitals reporting using PerformanceObserver
        if (typeof window !== "undefined" && "PerformanceObserver" in window) {
            // LCP
            try {
                const lcpObserver = new PerformanceObserver((list) => {
                    const entries = list.getEntries();
                    const lastEntry = entries[entries.length - 1] as PerformanceEntry & { renderTime?: number; loadTime?: number; };
                    if (lastEntry) {
                        const value = lastEntry.renderTime || lastEntry.loadTime || lastEntry.startTime;
                        reportWebVitals({ id: `lcp-${Date.now()}`, name: "LCP", value, rating: value <= 2500 ? "good" : value <= 4000 ? "needs-improvement" : "poor", delta: value, entries: [lastEntry], navigationType: "navigate" });
                    }
                });
                lcpObserver.observe({ type: "largest-contentful-paint", buffered: true });
            } catch {
                // LCP not supported
            }

            // FCP
            try {
                const fcpObserver = new PerformanceObserver((list) => {
                    for (const entry of list.getEntries()) {
                        if (entry.name === "first-contentful-paint") {
                            reportWebVitals({ id: `fcp-${Date.now()}`, name: "FCP", value: entry.startTime, rating: entry.startTime <= 1800 ? "good" : entry.startTime <= 3000 ? "needs-improvement" : "poor", delta: entry.startTime, entries: [entry], navigationType: "navigate" });
                        }
                    }
                });
                fcpObserver.observe({ type: "paint", buffered: true });
            } catch {
                // FCP not supported
            }

            // CLS
            try {
                let clsValue = 0;
                const clsObserver = new PerformanceObserver((list) => {
                    for (const entry of list.getEntries()) {
                        const layoutShiftEntry = entry as PerformanceEntry & { hadRecentInput?: boolean; value?: number; };
                        if (!layoutShiftEntry.hadRecentInput && layoutShiftEntry.value) {
                            clsValue += layoutShiftEntry.value;
                        }
                    }
                });
                clsObserver.observe({ type: "layout-shift", buffered: true });

                // Report CLS on page hide
                const reportCLS = () => {
                    reportWebVitals({ id: `cls-${Date.now()}`, name: "CLS", value: clsValue, rating: clsValue <= 0.1 ? "good" : clsValue <= 0.25 ? "needs-improvement" : "poor", delta: clsValue, entries: [], navigationType: "navigate" });
                };
                window.addEventListener("visibilitychange", () => {
                    if (document.visibilityState === "hidden") {
                        reportCLS();
                    }
                });
            } catch {
                // CLS not supported
            }
        }
    }, []);

    // Track route changes
    useEffect(() => {
        markPerformance(`route-start:${pathname}`);

        return () => {
            measurePerformance(`route:${pathname}`, `route-start:${pathname}`);
        };
    }, [pathname]);

    return null;
}
